import { createContext, useContext, useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, ArrowRight, MapPin } from "lucide-react";
import { track } from "@/lib/track";

const WalkthruContext = createContext({ start: () => {}, stop: () => {}, active: false });
export const useWalkthru = () => useContext(WalkthruContext);

// Two Smart Walkthrus keyed by name. Each step spotlights a real element and
// advances synchronously as the user clicks/fills through the flow. Steps with
// no reachable target (external popups) render a centered informational card.
const TOURS = {
  booking: [
    {
      key: "select",
      target: '[data-testid="pricing-plans-grid"]',
      title: "Choose your program",
      body: "Welcome! To begin, select your desired career strategy session or monthly premium plan from these options.",
      hint: "Click a plan's Donate button to continue.",
      advanceOn: '[data-testid^="pricing-cta-"]',
      advanceWhenAppears: '[data-testid="payment-terms-agree"]',
      scroll: true,
    },
    {
      key: "cta",
      target: '[data-testid="payment-terms-agree"]',
      title: "Proceed with purpose",
      body: "Click here to proceed. At The Next Move, standard fees are replaced by a mandatory donation that directly supports children's education and animal rescue centers.",
      hint: "Click Agree to continue.",
      advanceOn: '[data-testid="payment-terms-agree"]',
      advanceWhenAppears: '[data-testid="payment-details-step"]',
    },
    {
      key: "details",
      target: '[data-testid="payment-details-step"]',
      title: "Enter your details",
      body: "Please enter your Full Name, Email, and Phone Number. This secures your profile in our system.",
      hint: "Fill the form, then donate to continue.",
      advanceOn: '[data-testid="payment-continue-button"]',
      waitForPath: "/intake",
    },
    {
      key: "pay",
      target: null,
      centered: true,
      title: "Complete your donation",
      body: "Complete your financial contribution in the secure Razorpay window. Once processed, you will automatically unlock the scheduling system.",
      hint: "Waiting for your donation to complete.",
      waitForPath: "/intake",
      manualLabel: "I have donated",
    },
    {
      key: "schedule",
      target: '[data-testid="payment-confirmation"]',
      title: "Pick your slot",
      body: "Thank you for your contribution! You can now select your preferred date and time for your consultation with Rhea.",
      hint: "Confirm a date and time to continue.",
      advanceWhenAppears: '[data-testid="intake-form"]',
    },
    {
      key: "intake",
      target: '[data-testid="intake-form"]',
      title: "Complete your intake",
      body: "Final step! Complete this comprehensive intake form so Rhea can fully prepare your personalized strategy before your session.",
      hint: "Submit the assessment to finish.",
      endWhenAppears: '[data-testid="scorecard"]',
      lastStep: true,
    },
  ],
  corporate: [
    {
      key: "discover",
      target: '[data-testid="b2b-program-details"]',
      title: "Program discovery",
      body: "Welcome! We offer tailored programs designed specifically for institutes and organizations to elevate their teams.",
      hint: "Advancing automatically, or click Next.",
      autoAdvanceMs: 4000,
      scroll: true,
    },
    {
      key: "open",
      target: '[data-testid="b2b-enquiry-button"]',
      title: "Initiate the inquiry",
      body: "Ready to explore a custom solution? Click here to open the inquiry form and share your cohort's needs.",
      hint: "Click the button to open the form.",
      advanceOn: '[data-testid="b2b-enquiry-button"]',
      advanceWhenAppears: '[data-testid="enquiry-contact-fields"]',
    },
    {
      key: "contact",
      target: '[data-testid="enquiry-contact-fields"]',
      title: "Core contact information",
      body: "Please fill in these mandatory fields so our team knows who we are partnering with.",
      hint: "Add your name, organization and work email.",
      advanceWhenFilled: ['[data-testid="enquiry-name"]', '[data-testid="enquiry-email"]'],
    },
    {
      key: "cohort",
      target: '[data-testid="enquiry-message"]',
      title: "Cohort details",
      body: "Tell us about your group. Sharing your cohort size and specific goals helps us design the perfect customized program scope.",
      hint: "Describe your learners and desired outcomes.",
      advanceWhenFilled: ['[data-testid="enquiry-message"]'],
    },
    {
      key: "submit",
      target: '[data-testid="enquiry-submit"]',
      title: "Submit your inquiry",
      body: "Click here to submit your request to our administrative team.",
      hint: "Waiting for your inquiry to send.",
      advanceOn: '[data-testid="enquiry-submit"]',
      advanceWhenAppears: '[data-testid="enquiry-success"]',
    },
    {
      key: "next",
      target: '[data-testid="enquiry-success"]',
      title: "Next steps",
      body: "Thank you! You will receive an automated confirmation email shortly. Our team is now reviewing your goals and will follow up soon with a customized pricing proposal.",
      hint: "You are all set.",
      lastStep: true,
    },
  ],
  impact: [
    {
      key: "mission",
      target: '[data-testid="donate-tagline"]',
      title: "The dual-impact mission",
      body: "Your career investment changes lives. Every donation made to unlock a session directly funds education for children in need and vital supplies for stray dog rescue centers.",
      hint: "Advancing automatically, or click Next.",
      autoAdvanceMs: 5000,
      scroll: true,
    },
    {
      key: "session",
      target: '[data-testid="pricing-plans-grid"]',
      title: "Securing your session",
      body: "What do you get in return? Aside from helping those in need, your contribution immediately unlocks full access to your selected career strategy program and mentorship session.",
      hint: "Click Next to continue.",
    },
    {
      key: "tax",
      target: '[data-testid="payment-no-refund-disclaimer"]',
      title: "Tax exemption & receipts",
      body: "Because your payment is a charitable donation to an 80G-registered NGO, it is tax-deductible. You receive a donation receipt by email, and you can request your official 80G tax exemption certificate anytime from your student dashboard.",
      hint: "Click Next to continue.",
    },
    {
      key: "checkout",
      target: '[data-testid="pricing-plans-grid"]',
      title: "Proceed to checkout",
      body: "Pick your plan and enter your name, email and phone. Click Donate to make your next move! Your donation receipt, eligible for 80G tax exemption, is emailed to you, and your official 80G certificate can be requested anytime from your dashboard.",
      hint: "Pick a plan and donate when you are ready.",
      lastStep: true,
    },
  ],
};

const PAD = 8;
const EMPTY_STEPS = [];

export function WalkthruProvider({ children }) {
  const [tourKey, setTourKey] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const scrolledFor = useRef(-1);
  const advancingFrom = useRef(-1);

  const active = !!tourKey;
  const steps = useMemo(() => (tourKey ? TOURS[tourKey] : EMPTY_STEPS), [tourKey]);

  const start = useCallback((key = "booking") => {
    scrolledFor.current = -1;
    advancingFrom.current = -1;
    setStepIndex(0);
    setRect(null);
    const k = TOURS[key] ? key : "booking";
    track("Smart Walkthru", `Guide Me Launched: ${k}`);
    setTourKey(k);
  }, []);

  const stop = useCallback(() => {
    track("Smart Walkthru", "Walkthru Skipped");
    setTourKey(null);
    setRect(null);
    setStepIndex(0);
  }, []);

  // Idempotent advance: only advances once per source step, so the click
  // handler and the appearance/path detectors never double-increment.
  const advanceFrom = useCallback((from, total) => {
    if (advancingFrom.current >= from) return;
    advancingFrom.current = from;
    setTimeout(() => {
      setRect(null);
      setStepIndex((i) => (i >= total - 1 ? i : i + 1));
    }, 350);
  }, []);

  const endFrom = useCallback((from) => {
    if (advancingFrom.current >= from) return;
    advancingFrom.current = from;
    setTimeout(() => {
      setRect(null);
      setTourKey(null);
    }, 500);
  }, []);

  const manualNext = useCallback(() => {
    const total = steps.length;
    track("Smart Walkthru", "Walkthru Next");
    setRect(null);
    setStepIndex((i) => {
      advancingFrom.current = i;
      if (i >= total - 1) {
        setTourKey(null);
        return 0;
      }
      return i + 1;
    });
  }, [steps.length]);

  // Measurement + condition loop
  useEffect(() => {
    if (!active) return undefined;
    const total = steps.length;
    const step = steps[stepIndex];
    if (!step) return undefined;

    let autoTimer;
    if (step.autoAdvanceMs) {
      autoTimer = setTimeout(() => advanceFrom(stepIndex, total), step.autoAdvanceMs);
    }

    const filled = (sel) => {
      const el = document.querySelector(sel);
      if (!el) return false;
      const v = (el.value || "").trim();
      if (sel.includes("email")) return v.includes("@") && v.length >= 5;
      return v.length >= 2;
    };

    const id = setInterval(() => {
      const path = window.location.pathname;
      if (step.waitForPath && path.startsWith(step.waitForPath)) return advanceFrom(stepIndex, total);
      if (step.advanceWhenAppears && document.querySelector(step.advanceWhenAppears)) return advanceFrom(stepIndex, total);
      if (step.advanceWhenFilled && step.advanceWhenFilled.every(filled)) return advanceFrom(stepIndex, total);
      if (step.endWhenAppears && document.querySelector(step.endWhenAppears)) return endFrom(stepIndex);
      if (step.target) {
        const el = document.querySelector(step.target);
        if (el) {
          if (step.scroll && scrolledFor.current !== stepIndex) {
            scrolledFor.current = stepIndex;
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          const r = el.getBoundingClientRect();
          setRect((prev) => {
            if (
              prev &&
              Math.abs(prev.top - r.top) < 0.5 &&
              Math.abs(prev.left - r.left) < 0.5 &&
              Math.abs(prev.width - r.width) < 0.5 &&
              Math.abs(prev.height - r.height) < 0.5
            )
              return prev;
            return { top: r.top, left: r.left, width: r.width, height: r.height };
          });
          return undefined;
        }
      }
      setRect((prev) => (prev ? null : prev));
      return undefined;
    }, 60);

    return () => {
      clearInterval(id);
      if (autoTimer) clearTimeout(autoTimer);
    };
  }, [active, stepIndex, steps, advanceFrom, endFrom]);

  // Advance when the user actually clicks the highlighted control
  useEffect(() => {
    if (!active) return undefined;
    const total = steps.length;
    const step = steps[stepIndex];
    if (!step || !step.advanceOn) return undefined;
    const handler = (e) => {
      if (e.target.closest && e.target.closest(step.advanceOn)) advanceFrom(stepIndex, total);
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [active, stepIndex, steps, advanceFrom]);

  const value = { start, stop, active };

  return (
    <WalkthruContext.Provider value={value}>
      {children}
      {active &&
        steps[stepIndex] &&
        createPortal(
          <WalkthruOverlay
            step={steps[stepIndex]}
            stepIndex={stepIndex}
            total={steps.length}
            rect={rect}
            onNext={manualNext}
            onClose={stop}
          />,
          document.body
        )}
    </WalkthruContext.Provider>
  );
}

const WalkthruOverlay = ({ step, stepIndex, total, rect, onNext, onClose }) => {
  const useCentered = step.centered || !rect;
  const cardRef = useRef(null);
  const [cardH, setCardH] = useState(240);

  useLayoutEffect(() => {
    if (cardRef.current) setCardH(cardRef.current.offsetHeight);
  }, [step, rect]);

  let cardStyle;
  if (useCentered) {
    cardStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  } else {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 16;
    const cardW = Math.min(340, vw - 32);
    const spot = {
      top: rect.top - PAD,
      left: rect.left - PAD,
      width: rect.width + PAD * 2,
      height: rect.height + PAD * 2,
    };
    const clampX = (x) => Math.max(16, Math.min(x, vw - cardW - 16));
    const clampY = (y) => Math.max(16, Math.min(y, vh - cardH - 16));
    const overlaps = (top, left) =>
      !(left + cardW < spot.left || left > spot.left + spot.width || top + cardH < spot.top || top > spot.top + spot.height);

    // Try below, above, right, left; pick the first that fits the viewport and
    // does not overlap the highlighted target so clicks always reach it.
    const candidates = [
      { top: spot.top + spot.height + gap, left: clampX(spot.left), vFit: spot.top + spot.height + gap + cardH <= vh - 16 },
      { top: spot.top - gap - cardH, left: clampX(spot.left), vFit: spot.top - gap - cardH >= 16 },
      { top: clampY(spot.top), left: spot.left + spot.width + gap, hFit: spot.left + spot.width + gap + cardW <= vw - 16 },
      { top: clampY(spot.top), left: spot.left - gap - cardW, hFit: spot.left - gap - cardW >= 16 },
    ];
    const pick =
      candidates.find((c) => (c.vFit ?? c.hFit) && !overlaps(c.top, c.left)) ||
      candidates.find((c) => !overlaps(c.top, c.left));
    if (pick) {
      cardStyle = { top: clampY(pick.top), left: clampX(pick.left), width: cardW };
    } else {
      cardStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: cardW };
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9998, pointerEvents: "none" }} data-testid="walkthru-overlay">
      {useCentered ? (
        <div style={{ position: "fixed", inset: 0, background: "rgba(4,28,62,0.75)" }} />
      ) : (
        <div
          style={{
            position: "fixed",
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            borderRadius: 14,
            border: "2px solid #D94833",
            boxShadow: "0 0 0 9999px rgba(4,28,62,0.75)",
            transition: "top .2s ease, left .2s ease, width .2s ease, height .2s ease",
          }}
        />
      )}

      <div
        ref={cardRef}
        className="bg-white text-[#041C3E] shadow-2xl"
        style={{ position: "fixed", ...cardStyle, maxWidth: 340, pointerEvents: "auto", zIndex: 10000, borderRadius: 14 }}
      >
        <div className="p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#D94833]">
              <MapPin size={14} /> Step {stepIndex + 1} of {total}
            </div>
            <button
              data-testid="walkthru-close"
              onClick={onClose}
              className="text-[#3A4A63] hover:text-[#D94833] transition-colors"
              aria-label="Close walkthrough"
            >
              <X size={18} />
            </button>
          </div>
          <h3 className="font-head text-lg font-black mt-3 leading-tight" data-testid="walkthru-title">
            {step.title}
          </h3>
          <p className="text-sm text-[#3A4A63] mt-2 leading-relaxed" data-testid="walkthru-body">
            {step.body}
          </p>
          {step.hint && (
            <p className="mt-3 text-xs font-semibold text-[#041C3E] bg-[#FBEFEA] border border-[#D94833]/30 px-3 py-2">
              {step.hint}
            </p>
          )}
          <div className="mt-4 flex items-center justify-between">
            <button
              data-testid="walkthru-skip"
              onClick={onClose}
              className="text-xs font-semibold text-[#9CA3AF] hover:text-[#041C3E] transition-colors"
            >
              Skip tour
            </button>
            <button
              data-testid="walkthru-next"
              onClick={onNext}
              className="inline-flex items-center gap-2 bg-[#041C3E] text-white text-sm font-semibold px-4 py-2 hover:bg-[#D94833] transition-colors"
            >
              {step.lastStep ? "Finish" : step.manualLabel || "Next"}
              {!step.lastStep && <ArrowRight size={15} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
